import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import cloneDeep from 'lodash/cloneDeep';
import {
  DocumentStatus,
  PageGroup,
  PageGroupSource,
} from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNACollapsibleCard,
  DNACollapsibleActionMenu,
  DNAText,
  Iffy,
  InformationMessage,
} from '@alucio/lux-ui';
import { DNAChip, DNAThumbnail } from 'src/components/DNA';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { useDispatch } from 'src/state/redux';
import useMachineSelector, { composite } from 'src/hooks/useSelector';
import * as slideSettingsSelector from 'src/state/machines/publisherVersioning/SlideSettings/slideSettings.selectors';
import { getThumbURL } from 'src/utils/thumbnailHelpers';
import { isDocumentSealed } from 'src/utils/documentHelpers';
import { useSlideSettingsState } from 'src/state/machines/publisherVersioning/SlideSettings/SlideSettingsProvider';
import { RightGroupMenu } from '../Modals/SetGroupsModal/RightMenu';
import DeleteGroupModal from '../Modals/SetGroupsModal/RemoveGroupModal';
import { sortGroups } from 'src/utils/groupHelpers';
import { isSourceHiddenPage } from 'src/state/machines/publisherVersioning/SlideSettings/util';

export const S = StyleSheet.create({
  groupHeaderChip: {
    marginLeft: 8,
  },
  groupContainer: {
    marginVertical: 8,
  },
});

export const GroupHeader: React.FC<{group: PageGroup}> = ({ group }) => {
  return (
    <DNABox fill>
      <DNAText numberOfLines={1} testID="group-header-title">{group.name}</DNAText>
      <Iffy is={group.source === PageGroupSource.DOCUMENT}>
        <DNAChip.Beacon variant="SLIDE_IMPORTED" style={S.groupHeaderChip} />
      </Iffy>
    </DNABox>
  )
}

const RemoveImportedGroups : React.FC<{groups: PageGroup[]}> = ({ groups }) => {
  const hasImportedGroups = groups.some(group => group.source === PageGroupSource.DOCUMENT)

  const { removeImportedGroups } = useSlideSettingsState()

  if (!hasImportedGroups) return null
  return (
    <DNABox>
      <InformationMessage
        text="Groups were automatically imported"
        actionButtonFunction={() => removeImportedGroups()}
        actionButtonText="Delete all imported groups"
        variance="warning"
      />
    </DNABox>
  )
}

const Groups: React.FC = () => {
  const {
    service,
    currentDocumentVersionORM,
    latestContentPageData,
    thumbnailSizes,
    getSlideTitle,
    removeImportedGroups,
  } = useSlideSettingsState()

  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      slideSettingsSelector.versionDraft,
    ),
  )

  const dispatch = useDispatch();

  const isDocSealed = isDocumentSealed(currentDocumentVersionORM.relations.documentORM);

  const isVersionPublished = currentDocumentVersionORM
    .model
    .status === DocumentStatus.PUBLISHED

  const isVersionPublishedOrSealed = (isVersionPublished || isDocSealed)

  const pages = useMemo(() => isVersionPublishedOrSealed
    ? currentDocumentVersionORM.meta.allPages
    : cond.versionDraft.pages
  , [isVersionPublishedOrSealed, currentDocumentVersionORM])

  const groups = useMemo<PageGroup[]>(() => {
    const pageMap = new Map(pages.map(page => [page.pageId, page]));
    return (cloneDeep(isVersionPublishedOrSealed
      ? currentDocumentVersionORM.model.pageGroups
      : cond.versionDraft.pageGroups)
      ?.filter(group => group.source !== PageGroupSource.DOCUMENT ||
        group.pageIds?.some(pageId => !isSourceHiddenPage(pageMap.get(pageId))))
      ?.sort(sortGroups())) || []
  }, [isVersionPublishedOrSealed, currentDocumentVersionORM, cond.versionDraft.pageGroups, pages])

  const removeGroup = (groupName: string) => {
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: true,
      component: (props) => (
        <DeleteGroupModal
          groupName={groupName}
          onSuccess={() => removeImportedGroups(groupName)}
          {...props}
        />
      ),
    }))
  }

  if (!groups) return null
  return (
    <DNABox appearance="col">
      <Iffy is={currentDocumentVersionORM.model.status === DocumentStatus.NOT_PUBLISHED}>
        <RemoveImportedGroups groups={groups} />
      </Iffy>
      {
        groups.map((group) => {
          return (
            <DNABox
              key={group.id}
              appearance="col"
              style={S.groupContainer}
            >
              <DNACollapsibleCard
                isCollapsed
                variant={'publisherReadonly'}
                headerTitle={<GroupHeader group={group} />}
              >
                <DNACollapsibleActionMenu>
                  <RightGroupMenu
                    pages={group.pageIds?.length || 0}
                    locked={group.locked}
                    onDelete={
                      currentDocumentVersionORM.model.status === DocumentStatus.NOT_PUBLISHED
                        ? () => { removeGroup(group.name) }
                        : undefined
                    }
                  />
                </DNACollapsibleActionMenu>
                <DNABox
                  fill
                  appearance="row"
                  wrap="start"
                  spacing="lg"
                  childStyle={{ marginBottom: 32 }}
                >
                  {group.pageIds?.map((pageId, idx) => {
                    const page = pages.find((page) => page.pageId === pageId)
                    if (!page || !group || !group.pageIds) {
                      return null
                    }
                    const thumbURL = getThumbURL(currentDocumentVersionORM, page.number, latestContentPageData)

                    /**
                     * this is more of a hack for some reason all the elements have a margin of
                     * 32 on the right all except the last one, with this hack that margin is being injected
                     */
                    const styleLatest = idx === group.pageIds.length - 1 ? { marginRight: 32 } : {}
                    return (
                      <DNABox key={`${group.id}-${pageId}-${idx}`} appearance="col" style={styleLatest}>
                        <DNAThumbnail
                          key={thumbURL}
                          s3URL={thumbURL}
                          useLoadingIndicator
                          size={thumbnailSizes.thumbnailSize}
                          mode={DNAThumbnail.Modes.READ_ONLY}
                          variant={DNAThumbnail.Variants.INFO}
                          pageNumber={page.number}
                          isHidden={page.isHidden}
                          sourceIsHidden={page.sourceIsHidden}
                          thumbnailTitle={getSlideTitle(page.number)}
                        />
                      </DNABox>
                    )
                  })
                }
                </DNABox>
              </DNACollapsibleCard>
            </DNABox>
          )
        })
      }
    </DNABox>
  )
}

export default Groups
